// Data contracts for the Logic Decomposer dashboard, transcribed from
// "Logic Decomposer v3 - Dev Handoff.md" §4. These are the shapes the backend must
// return; the module renders fixtures with the same types until the endpoints exist.

import type { Disposition, Complexity, ArtifactStatus, Outcome } from './tokens';

export type View = 'portfolio' | 'framework' | 'consolidation' | 'phases' | 'rules' | 'review' | 'attestation' | 'audit';
// Single taxonomy (task 1218070214136694): the Portfolio tabs are the six engine outcomes
// (Box 1-4 / Consolidate / Decommission / Queue) + All — the same words Framework Mapping and
// the Review Queue use. Was 'migrate' | 'document' | … (a second, collapsed vocabulary).
export type MainTab = 'all' | Outcome;
export type PanelTab = 'overview' | 'lineage' | 'logic' | 'insights';
export type TranslateTab = 'requirements' | 'pseudocode' | 'sql' | 'python' | 'dax';
export type LogicFilter = 'all' | 'hc' | 'fn';

// Handoff §5.2: "Order: Requirements → Pseudocode → SQL → Python → DAX. Requirements
// is default." §4.3: it is the safe default — least model-risk, every other target
// derives from it. Order is contractual, not cosmetic.
export const TRANSLATE_TABS: TranslateTab[] = ['requirements', 'pseudocode', 'sql', 'python', 'dax'];

/** §4.1 — a row in the Portfolio grid. */
export interface ArtifactRow {
  id: string;
  name: string;
  meta_line: string;              // rendered as-is in the row
  owner: string;
  assignee: string;
  timing_cat: 1 | 2 | 3;            // from the Apparity join
  box?: 1 | 2 | 3 | 4;            // 1..4 for the box outcomes; undefined for consolidate/decommission/queue
  // Single taxonomy: `outcome` is the engine's six-outcome disposition (Box 1-4 / Consolidate /
  // Decommission / Queue) — what the pill and the tab filter use. `disposition` is the LEGACY
  // 4-box alias (Migrate/Document/…), kept for the export vocabulary only.
  outcome: Outcome;
  disposition: Disposition;
  target_state_label?: string;
  confidence: number | null;      // 0..1; null when the record is in review (no score yet)
  complexity: Complexity;
  complexity_pct: number;         // 0..100, for the progress bar
  status: ArtifactStatus;
}

export interface ArtifactList {
  items: ArtifactRow[];
  total: number;
  // Keyed by the six engine outcomes — the same keys the Portfolio tabs iterate.
  counts_by_outcome: Record<Outcome, number>;
}

/** §4.2 — one extracted-logic row in the Business Logic tab. */
export interface LogicRow {
  tag: 'HC' | 'FN';               // hardcode | function
  location: string;               // 'Sheet2!F14 · Materiality threshold'
  code_html: string;              // server-rendered highlighted HTML — sanitize before injecting
  /** How many source cells/statements share this row's shape (fill-down formulas collapse to
   *  one row — see businessLogicView.js). 1 when the rule appears exactly once. */
  repeat_count?: number;
}

export interface Signal {
  label: string;
  value: string;
  level: 'high' | 'medium' | 'low';
  tooltip: string;
}

/** §4.2 — the side-panel detail payload. */
export interface ArtifactDetail {
  id: string;
  name: string;
  owner: string;
  assignee: string;
  timing_cat: 1 | 2 | 3;
  outcome: Outcome;               // single taxonomy — see ArtifactRow.outcome
  disposition: Disposition;       // legacy 4-box alias (export vocabulary only)
  target_state_label?: string;
  confidence: number;
  // Overview
  rationale: string;              // scoreWorkflow.rationale
  purpose: string;                // workflow.purpose_summary
  last_modified: string;
  usage_summary: string;
  // Lineage
  sources: string[];
  targets: string[];
  transforms: { op: string; desc: string }[];
  transform_count: number;
  // Business logic
  // `total_statements` is the raw formula-cell + VBA-procedure count, fill-down repeats
  // included. `distinct_rules` is exactly `logic_rows.length` — the number a reviewer can
  // count in the detail view. The two are never the same number on a real workbook and
  // must not be swapped for each other (QA finding 2026-08-03).
  counts: { formulas: number; macros: number; hardcodes: number; lookups: number; total_statements: number; distinct_rules: number };
  /** Distinct rule rows omitted from `logic_rows` past the server's per-file cap (0 when nothing was cut). */
  truncated?: number;
  logic_rows: LogicRow[];
  // Insights
  signals: Signal[];
  overlaps: { name: string; similarity: number }[];
  file_attached?: boolean;
  gates_fired?: { id: string; action: string }[];
  /** Present once the artifact came from a stored extraction run — lets a translation be saved. */
  run_id?: string;
  /** Translations already generated for this run, keyed by target. */
  translations?: Partial<Record<TranslateTab, Translation>>;
  /** Set when the analyzed file looks like an EUC inventory register (not a workflow). The dashboard
   *  warns and steers to "Load inventory" instead of creating a zero-logic EUC (§4; QA 2026-09-23). */
  register_signal?: RegisterSignal;
}

/** Result of the deterministic inventory-register guard on an analyzed .xlsx (registerDetection.js). */
export interface RegisterSignal {
  is_register: boolean;
  reason?: string;
  matched_columns?: string[];
  sheet_named_inventory?: boolean;
}

/** §4.3 — GET/POST …/artifacts/:id/translation?target=… */
export interface Translation {
  target: TranslateTab;
  translation_html: string;       // server-rendered highlighted HTML
  generated_at: string;
  model: string;
  // Not in the v3 handoff: translation became LD-native on 2026-07-17 (Ryan), and the
  // backend's adversarial review pass can hand back a translation it could not confirm.
  // The panel is human-reviewable by design, so an unconfirmed translation is shown WITH
  // its open issues rather than hidden or silently retried.
  needs_human_review?: boolean;
  review_issues?: { severity: 'critical' | 'major' | 'minor'; claim: string }[];
}

/** §4.5 — GET …/framework-summary */
export interface FrameworkSummary {
  smbc_categories: { id: 1 | 2 | 3; title: string; count: number; subtitle: string }[];
  boxes: {
    id: 1 | 2 | 3 | 4;
    title: string;
    risk: 'HIGH' | 'MEDIUM' | 'LOW';
    count: number;
    risk_desc?: string;
    from_cats: { cat: 1 | 2 | 3; level: 'fit' | 'partial' }[];
  }[];
}

/** §4.6 — GET /api/engagement/artifacts?phase=… */
// 'External' (task 1218070214136694, subtask 6): a SOW deliverable produced OUTSIDE Jarvis (legal
// policy addendum, Deloitte review protocol) — shown honestly, never a Jarvis download.
export type ArtifactState = 'Ready' | 'Draft' | 'Queued' | 'Planned' | 'External';

export interface EngagementArtifact {
  id: string;
  phase: 1 | 2 | 3 | 4;
  name: string;
  format: 'PDF' | 'DOCX' | 'XLSX' | 'CSV' | 'JSON' | 'MD' | 'PBIX' | 'ZIP';
  size_label?: string;
  state: ArtifactState;
  updated_label?: string;
}

// §4.6: "Only two states allow download: Ready and Draft."
export const isDownloadable = (s: ArtifactState): boolean => s === 'Ready' || s === 'Draft';

export interface PhaseDef {
  id: 1 | 2 | 3 | 4;
  title: string;
  statusLine: string;
  progress: 'complete' | 'active' | 'queued';
  artifacts: EngagementArtifact[];
}
